import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles, categories } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const currentCategory = categories.find((c) => c.slug === article?.category);
  const relatedArticles = articles
    .filter((a) => a.category === article?.category && a.slug !== slug)
    .slice(0, 3);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | 一区二区三区导航指南`;
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Header />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">文章未找到</p>
          <Link to="/" className="text-primary-500 mt-4 inline-block text-sm">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const catColorMap: Record<string, string> = {
    accent: 'bg-accent-100 text-accent-700',
    primary: 'bg-primary-100 text-primary-700',
    secondary: 'bg-secondary-100 text-secondary-700',
  };
  const catColor = currentCategory ? catColorMap[currentCategory.color] || catColorMap.primary : catColorMap.primary;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory?.name || '', href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchemaData = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qixuan2023.cn/' },
      { '@type': 'ListItem', position: 2, name: currentCategory?.name || '', item: `https://qixuan2023.cn/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchemaData} />
      <Header />

      <main className="pt-16">
        <article className="w-full bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 pt-8 pb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${catColor}`}>
                {currentCategory?.name}
              </span>
              <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
              {article.excerpt}
            </p>

            <div className="flex items-center gap-4 pb-8 border-b border-background-200">
              <span className="text-xs text-foreground-400">{article.author}</span>
              <span className="text-xs text-foreground-300">·</span>
              <span className="text-xs text-foreground-400">{article.date}</span>
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
            <div
              className="prose prose-sm md:prose-base max-w-none
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                prose-p:text-foreground-700 prose-p:leading-relaxed
                prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600
                prose-strong:text-foreground-900
                prose-li:text-foreground-700 prose-li:leading-relaxed
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关文章推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="bg-background-50 rounded-2xl border border-background-200 p-5 transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                  >
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${catColor}`}>
                      {currentCategory?.name}
                    </span>
                    <h4 className="font-heading text-base font-semibold text-foreground-900 mt-3 mb-2 line-clamp-2 hover:text-primary-500 transition-colors">
                      {ra.title}
                    </h4>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {ra.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}